import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import LightBackground from '@/components/LightBackground';
import { 
  Users, 
  CreditCard, 
  TrendingUp, 
  Activity,
  ArrowLeft,
  DollarSign,
  Target,
  Award,
  AlertTriangle,
  Loader2,
  RefreshCw,
  BarChart3,
  PieChart,
  LineChart,
  Sparkles,
  Coins
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import {
  fetchClients,
  Client,
  getActiveClients,
  getExpiringClients,
  getExpiredClients,
  getDaysUntilExpiration,
  calculateClientProgress,
} from '@/services/clientService';
import { mockClients } from '@/data/clientsData';
import { supabase } from '@/integrations/supabase/client';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts';
import WebsiteTrafficSection from '@/components/admin/WebsiteTrafficSection';
import { useWebsiteAnalytics, type DatePreset } from '@/hooks/useWebsiteAnalytics';

const CREDIT_PACK_PRICES: Record<string, number> = {
  starter: 2.99, growth: 9.99, pro: 22.99, elite: 69.99, ultimate: 499,
};
const COST_PER_CREDIT = 0.01;

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const { user, role, loading: authLoading, signOut } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [useMockData, setUseMockData] = useState(false);
  const [creditRevenue, setCreditRevenue] = useState(0);
  const [aiApiCost, setAiApiCost] = useState(0);
  const [totalCreditsUsed, setTotalCreditsUsed] = useState(0);
  
  const { analytics: websiteAnalytics, loading: analyticsLoading, error: analyticsError, refetch: refetchAnalytics, dateRange: analyticsDates, preset: analyticsPreset, setPreset: setAnalyticsPreset } = useWebsiteAnalytics();

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
        navigate('/dashboard');
      }
    }
  }, [user, role, authLoading, navigate]);

  const loadClients = async () => {
    setLoading(true);
    try {
      const data = await fetchClients();
      if (data.length === 0) {
        setUseMockData(true);
        const convertedMockData: Client[] = mockClients.map((m) => ({
          id: m.id.toString(),
          user_id: null,
          name: m.name,
          email: m.email,
          plan: m.plan,
          plan_price: m.planPrice,
          status: m.status,
          paid: m.status !== 'expired',
          subscription_start: m.subscriptionStart,
          subscription_end: m.subscriptionEnd,
          last_active_at: m.lastActiveDate.toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          academy_progress: m.academyProgress.map((ap) => ({
            id: `${m.id}-${ap.academyId}`,
            client_id: m.id.toString(),
            academy_id: ap.academyId,
            academy_name: ap.academyName,
            completed_modules: ap.completedModules,
            total_modules: ap.totalModules,
          })),
        }));
        setClients(convertedMockData);
      } else {
        setUseMockData(false);
        setClients(data);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      setUseMockData(true);
    }
    setLoading(false);
  };

  const loadAIData = useCallback(async () => {
    try {
      const [purchasesRes, usageRes] = await Promise.all([
        supabase.from('ai_usage_log').select('tool_type').like('tool_type', 'credit_purchase_%'),
        supabase.from('ai_usage_log').select('credits_used, tool_type').not('tool_type', 'like', 'credit_purchase_%'),
      ]);

      let rev = 0;
      (purchasesRes.data || []).forEach(log => {
        const pack = log.tool_type.replace('credit_purchase_', '');
        rev += CREDIT_PACK_PRICES[pack] || 0;
      });
      setCreditRevenue(rev);

      const used = (usageRes.data || []).reduce((sum, l) => sum + Math.max(0, l.credits_used), 0);
      setTotalCreditsUsed(used);
      setAiApiCost(used * COST_PER_CREDIT);
    } catch (e) {
      console.error('Error loading AI data:', e);
    }
  }, []);

  useEffect(() => {
    if (user && role === 'admin') {
      loadClients();
      loadAIData();
    }
  }, [user, role]);

  // --- Filter test accounts ---
  const isTestAccount = (c: { email: string; plan: string }) => {
    const email = c.email.toLowerCase();
    return email.includes('test') || email.includes('example') || c.plan.toLowerCase().includes('test');
  };
  const realClients = clients.filter(c => !isTestAccount(c));
  const testAccounts = clients.filter(c => isTestAccount(c));
  const now = new Date();

  // --- Plan categories ---
  const monthlyClients = realClients.filter(c => c.plan.toLowerCase().includes('monthly'));
  const lifetimeClients = realClients.filter(c => c.plan.toLowerCase().includes('lifetime'));
  const freemiumClients = realClients.filter(c => c.plan.toLowerCase() === 'freemium' || (!c.paid && !c.amount_paid));

  const activeMonthly = monthlyClients.filter(c => c.paid && (c.status === 'active' || c.status === 'expiring') && new Date(c.subscription_end) >= now);
  const activeLifetime = lifetimeClients.filter(c => c.paid && (c.status === 'active' || c.status === 'expiring'));
  const activeAll = activeMonthly.length + activeLifetime.length;

  const expiredClients = realClients.filter(c => c.status === 'expired' || new Date(c.subscription_end) < now);

  // --- Revenue (real, using amount_paid) ---
  const totalRevenue = realClients
    .filter(c => c.paid && c.amount_paid && c.amount_paid > 0)
    .reduce((sum, c) => sum + (c.amount_paid || 0), 0) + creditRevenue;

  const subscriptionRevenue = realClients
    .filter(c => c.paid && c.amount_paid && c.amount_paid > 0)
    .reduce((sum, c) => sum + (c.amount_paid || 0), 0);

  const netRevenue = totalRevenue - aiApiCost;

  // --- Plan distribution pie chart ---
  const planDistribution = [
    { name: 'Monthly', value: monthlyClients.length, color: '#3b82f6' },
    { name: 'Lifetime', value: lifetimeClients.length, color: '#8b5cf6' },
    { name: 'Freemium', value: freemiumClients.length, color: '#6b7280' },
  ].filter(d => d.value > 0);

  // --- Status distribution ---
  const statusData = [
    { name: 'Active', value: activeAll, color: '#22c55e' },
    { name: 'Freemium', value: freemiumClients.length, color: '#6b7280' },
    { name: 'Expired', value: expiredClients.length, color: '#ef4444' },
    { name: 'Test', value: testAccounts.length, color: '#94a3b8' },
  ].filter(d => d.value > 0);

  // --- Real revenue timeline (last 6 months from actual data) ---
  const revenueHistory = (() => {
    const months: { month: string; revenue: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
      const label = d.toLocaleString('en', { month: 'short' });
      const rev = realClients
        .filter(c => {
          if (!c.paid || !c.amount_paid || c.amount_paid <= 0) return false;
          const subStart = new Date(c.subscription_start);
          return subStart >= d && subStart <= monthEnd;
        })
        .reduce((sum, c) => sum + (c.amount_paid || 0), 0);
      months.push({ month: label, revenue: rev });
    }
    return months;
  })();

  // --- Academy progress ---
  const academyProgressData = (() => {
    const academyMap: Record<string, { name: string; totalCompleted: number; totalModules: number; clientCount: number }> = {};
    
    clients.forEach(client => {
      client.academy_progress?.forEach(progress => {
        if (!academyMap[progress.academy_id]) {
          academyMap[progress.academy_id] = { name: progress.academy_name, totalCompleted: 0, totalModules: 0, clientCount: 0 };
        }
        academyMap[progress.academy_id].totalCompleted += progress.completed_modules;
        academyMap[progress.academy_id].totalModules += progress.total_modules;
        academyMap[progress.academy_id].clientCount += 1;
      });
    });

    return Object.values(academyMap).map(a => ({
      name: a.name.split(' ').slice(0, 2).join(' '),
      fullName: a.name,
      avgProgress: a.totalModules > 0 ? Math.round((a.totalCompleted / a.totalModules) * 100) : 0,
    }));
  })();

  // --- Progress distribution ---
  const progressDistribution = (() => {
    const ranges = [
      { range: '0-20%', min: 0, max: 20, count: 0 },
      { range: '21-40%', min: 21, max: 40, count: 0 },
      { range: '41-60%', min: 41, max: 60, count: 0 },
      { range: '61-80%', min: 61, max: 80, count: 0 },
      { range: '81-100%', min: 81, max: 100, count: 0 },
    ];
    clients.forEach(client => {
      const progress = calculateClientProgress(client);
      const range = ranges.find(r => progress >= r.min && progress <= r.max);
      if (range) range.count += 1;
    });
    return ranges;
  })();

  // --- Top performers ---
  const topClients = [...realClients]
    .map(c => ({ ...c, progress: calculateClientProgress(c) }))
    .sort((a, b) => b.progress - a.progress)
    .slice(0, 5);

  // --- Clients needing attention ---
  const attentionClients = realClients
    .filter(c => {
      const days = getDaysUntilExpiration(c.subscription_end);
      const progress = calculateClientProgress(c);
      return (days > 0 && days <= 30) || progress < 20;
    })
    .slice(0, 5);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      <LightBackground variant="subtle" />

      {/* Header */}
      <motion.header
        className="relative z-20 p-6 flex justify-between items-center border-b border-border bg-white/80 backdrop-blur-sm"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center gap-4">
          <Link to="/admin">
            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <span className="text-lg font-semibold text-foreground">
            Faceless Aura <span className="text-primary">Club</span>
          </span>
          <div className="h-6 w-px bg-border" />
          <span className="text-sm font-medium text-primary">Analytics Dashboard</span>
          {useMockData && (
            <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
              Demo Data
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-muted-foreground hover:text-foreground"
            onClick={() => { loadClients(); loadAIData(); refetchAnalytics(); }}
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
          <span className="text-sm text-muted-foreground">{user?.email}</span>
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary" onClick={signOut}>
            Sign Out
          </Button>
        </div>
      </motion.header>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        <motion.div className="mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
          <h1 className="text-2xl md:text-3xl font-light tracking-wide mb-2 text-foreground">Analytics & Insights</h1>
          <p className="text-muted-foreground text-sm">Comprehensive overview of platform performance</p>
        </motion.div>

        {/* Website Traffic Section */}
        <WebsiteTrafficSection
          analytics={websiteAnalytics}
          loading={analyticsLoading}
          error={analyticsError}
          preset={analyticsPreset}
          onPresetChange={setAnalyticsPreset}
          onRefresh={refetchAnalytics}
          dateRange={analyticsDates}
        />

        {/* Revenue & Financial Overview */}
        <motion.div className="mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-light text-foreground">Revenue & Financial</h2>
          </div>
        </motion.div>

        <motion.div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.35 }}>
          {[
            { label: 'Total Revenue', value: `$${totalRevenue.toFixed(2)}`, icon: DollarSign, color: 'text-green-600' },
            { label: 'Subscriptions', value: `$${subscriptionRevenue.toFixed(2)}`, icon: CreditCard, color: 'text-blue-600' },
            { label: 'Credit Sales', value: `$${creditRevenue.toFixed(2)}`, icon: Coins, color: 'text-amber-600' },
            { label: 'AI API Costs', value: `-$${aiApiCost.toFixed(2)}`, icon: Sparkles, color: 'text-red-500' },
            { label: 'Net Revenue', value: `$${netRevenue.toFixed(2)}`, icon: TrendingUp, color: netRevenue >= 0 ? 'text-emerald-600' : 'text-red-500' },
            { label: 'Credits Used', value: totalCreditsUsed.toLocaleString(), icon: Activity, color: 'text-purple-500' },
          ].map((stat, index) => (
            <div key={index} className="p-4 border border-border/30 bg-white rounded-xl shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{stat.label}</span>
              </div>
              <p className={`text-xl font-bold text-foreground`}>{stat.value}</p>
            </div>
          ))}
        </motion.div>

        {/* Client Overview */}
        <motion.div className="mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}>
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-light text-foreground">Client Overview</h2>
          </div>
        </motion.div>

        <motion.div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.45 }}>
          {[
            { label: 'Active (All)', value: activeAll, color: 'text-green-600' },
            { label: 'Monthly Active', value: activeMonthly.length, color: 'text-blue-600' },
            { label: 'Lifetime', value: activeLifetime.length, color: 'text-purple-600' },
            { label: 'Freemiums', value: freemiumClients.length, color: 'text-slate-500' },
            { label: 'Expired / Off', value: expiredClients.length, color: 'text-red-500' },
            { label: 'Test Accounts', value: testAccounts.length, color: 'text-slate-400' },
          ].map((stat, index) => (
            <div key={index} className="p-4 border border-border/30 bg-white rounded-xl shadow-sm">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{stat.label}</span>
              <p className={`text-2xl font-bold mt-1 ${stat.color}`}>{stat.value}</p>
            </div>
          ))}
        </motion.div>

        {/* Charts Row 1 */}
        <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }}>
          {/* Revenue Trend */}
          <div className="p-6 border border-border/30 bg-white rounded-xl shadow-sm">
            <div className="flex items-center gap-2 mb-6">
              <LineChart className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-medium text-foreground">Revenue Trend (Last 6 Months)</h3>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueHistory}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                    formatter={(value: number) => [`$${value.toFixed(2)}`, 'Revenue']}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" fillOpacity={1} fill="url(#colorRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Plan & Status Distribution */}
          <div className="p-6 border border-border/30 bg-white rounded-xl shadow-sm">
            <div className="flex items-center gap-2 mb-6">
              <PieChart className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-medium text-foreground">Plan Distribution</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-2">By Plan</p>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie data={planDistribution} cx="50%" cy="50%" innerRadius={30} outerRadius={55} paddingAngle={5} dataKey="value">
                        {planDistribution.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1 mt-2">
                  {planDistribution.map(d => (
                    <div key={d.name} className="flex items-center gap-2 text-xs">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                      <span className="text-foreground">{d.name}</span>
                      <span className="text-muted-foreground ml-auto">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-2">By Status</p>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie data={statusData} cx="50%" cy="50%" innerRadius={30} outerRadius={55} paddingAngle={5} dataKey="value">
                        {statusData.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1 mt-2">
                  {statusData.map(d => (
                    <div key={d.name} className="flex items-center gap-2 text-xs">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                      <span className="text-foreground">{d.name}</span>
                      <span className="text-muted-foreground ml-auto">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Charts Row 2 */}
        <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.55 }}>
          {/* Academy Progress */}
          <div className="p-6 border border-border/30 bg-white rounded-xl shadow-sm">
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-medium text-foreground">Academy Completion Rates</h3>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={academyProgressData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} domain={[0, 100]} />
                  <YAxis dataKey="name" type="category" stroke="hsl(var(--muted-foreground))" fontSize={10} width={80} />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} formatter={(value: number) => [`${value}%`, 'Avg Progress']} />
                  <Bar dataKey="avgProgress" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Progress Distribution */}
          <div className="p-6 border border-border/30 bg-white rounded-xl shadow-sm">
            <div className="flex items-center gap-2 mb-6">
              <Target className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-medium text-foreground">Client Progress Distribution</h3>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={progressDistribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis dataKey="range" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} formatter={(value: number) => [`${value} clients`, 'Count']} />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>

        {/* Lists Row */}
        <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.6 }}>
          {/* Top Performers */}
          <div className="p-6 border border-border/30 bg-white rounded-xl shadow-sm">
            <div className="flex items-center gap-2 mb-6">
              <Award className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-medium text-foreground">Top Performing Clients</h3>
            </div>
            <div className="space-y-3">
              {topClients.map((client, index) => (
                <div key={client.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/20">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center font-medium">
                      {index + 1}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground">{client.name}</p>
                      <p className="text-xs text-muted-foreground">{client.plan}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-muted/20 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-primary/60 to-primary rounded-full" style={{ width: `${client.progress}%` }} />
                    </div>
                    <span className="text-xs text-muted-foreground w-8">{client.progress}%</span>
                  </div>
                </div>
              ))}
              {topClients.length === 0 && <p className="text-center text-muted-foreground text-sm py-8">No client data yet</p>}
            </div>
          </div>

          {/* Clients Needing Attention */}
          <div className="p-6 border border-border/30 bg-white rounded-xl shadow-sm">
            <div className="flex items-center gap-2 mb-6">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h3 className="text-sm font-medium text-foreground">Clients Needing Attention</h3>
            </div>
            <div className="space-y-3">
              {attentionClients.length > 0 ? attentionClients.map((client) => {
                const days = getDaysUntilExpiration(client.subscription_end);
                const progress = calculateClientProgress(client);
                const reasons: string[] = [];
                if (days > 0 && days <= 30) reasons.push(`Expires in ${days}d`);
                if (progress < 20) reasons.push(`${progress}% progress`);

                return (
                  <div key={client.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-amber-200/30">
                    <div>
                      <p className="text-sm font-medium text-foreground">{client.name}</p>
                      <p className="text-xs text-muted-foreground">{client.plan}</p>
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end max-w-[200px]">
                      {reasons.map((reason, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                          {reason}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              }) : (
                <p className="text-center text-muted-foreground text-sm py-8">All clients are in good standing!</p>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
